/**
 * Where a character sits on screen inside an `<input>` or `<textarea>`.
 *
 * There is no browser API for this — a text field's contents are not in the DOM,
 * so nothing can be measured with a Range the way it can in a contenteditable.
 * The standard answer, and the one here, is to lay the same text out again in a
 * hidden element that copies every property that affects layout, and measure a
 * marker inside *that*.
 *
 * It exists so the alternatives picker can open under the word it is offering
 * readings of. Anchoring to the field instead is what it did first, and on a
 * ten-line textarea that put the list a long way from the word in question.
 */

/**
 * Everything that changes where a glyph lands.
 *
 * Missing one shows up as a picker that drifts further from the word the longer
 * the line gets, which is a hard bug to see and an easy one to cause.
 */
const MIRRORED = [
  'boxSizing',
  'width',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'fontStyle',
  'fontVariant',
  'fontWeight',
  'fontStretch',
  'fontSize',
  'fontFamily',
  'lineHeight',
  'letterSpacing',
  'wordSpacing',
  'textIndent',
  'textTransform',
  'textAlign',
  'direction',
  'tabSize',
] as const;

export interface CaretRect {
  left: number;
  top: number;
  bottom: number;
}

/**
 * Viewport coordinates of the character at `index`.
 *
 * The field's own scroll offsets are subtracted, so a caret several lines down a
 * scrolled textarea still reports where it actually appears rather than where it
 * would appear if nothing had scrolled.
 */
export function caretRect(
  field: HTMLInputElement | HTMLTextAreaElement,
  index: number,
): CaretRect {
  const style = window.getComputedStyle(field);
  const mirror = document.createElement('div');

  for (const property of MIRRORED) {
    mirror.style[property] = style[property];
  }

  // A single-line input never wraps; a textarea wraps exactly as it is shown.
  mirror.style.whiteSpace = field instanceof HTMLTextAreaElement ? 'pre-wrap' : 'pre';
  mirror.style.overflowWrap = 'break-word';
  mirror.style.position = 'absolute';
  mirror.style.top = '0';
  mirror.style.left = '-9999px';
  mirror.style.visibility = 'hidden';
  mirror.style.height = 'auto';

  mirror.textContent = field.value.slice(0, index);

  /*
   * The marker carries the *rest* of the text, not a bare caret.
   *
   * Its own position is what gets measured, and a zero-width marker at a line
   * break would be laid out at the end of the finished line rather than the
   * start of the next one — which is precisely where a wrapped word begins.
   */
  const marker = document.createElement('span');
  marker.textContent = field.value.slice(index) || '.';
  mirror.append(marker);

  document.body.append(mirror);
  const offsetLeft = marker.offsetLeft;
  const offsetTop = marker.offsetTop;
  mirror.remove();

  const bounds = field.getBoundingClientRect();
  const lineHeight = Number.parseFloat(style.lineHeight) || Number.parseFloat(style.fontSize) * 1.4;

  const left = bounds.left + offsetLeft - field.scrollLeft;
  const top = bounds.top + offsetTop - field.scrollTop;

  return { left, top, bottom: top + lineHeight };
}
